import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './FeedbackForm.css';

const FeedbackForm = () => {
  const [feedbackType, setFeedbackType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const { getAuthHeaders } = useAuth();

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/bmp', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select an image file (PNG, JPG, JPEG, GIF, BMP, WebP)');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const authHeaders = getAuthHeaders();
      
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('type', feedbackType);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('priority', priority);
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await fetch('/feedback/', {
        method: 'POST',
        headers: {
          ...authHeaders
          // Don't set Content-Type - let browser set it for FormData
        },
        body: formData,
      });

      if (response.ok) {
        setSubmitted(true);
        setTitle('');
        setDescription('');
        setPriority('medium');
        setSelectedFile(null);
        setFilePreview(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(error.message || 'Failed to submit feedback. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="feedback-success">
        <h2>Thank You!</h2>
        <p>Your feedback has been submitted successfully.</p>
        <button onClick={() => setSubmitted(false)}>Submit Another</button>
      </div>
    );
  }

  return (
    <div className="feedback-form-container">
      <h2>Submit Feedback</h2>
      <form onSubmit={handleSubmit} className="feedback-form">
        <div className="form-group">
          <label>Feedback Type:</label>
          <div className="feedback-type-buttons">
            <button
              type="button"
              className={`type-button ${feedbackType === 'bug' ? 'active' : ''}`}
              onClick={() => setFeedbackType('bug')}
            >
              🐛 Bug Report
            </button>
            <button
              type="button"
              className={`type-button ${feedbackType === 'feature' ? 'active' : ''}`}
              onClick={() => setFeedbackType('feature')}
            >
              💡 Feature Request
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="title">Title:</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`Enter ${feedbackType === 'bug' ? 'bug' : 'feature'} title`}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={`Describe the ${feedbackType === 'bug' ? 'bug' : 'feature request'} in detail`}
            required
            rows="6"
          />
        </div>

        <div className="form-group">
          <label htmlFor="priority">Priority:</label>
          <select
            id="priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="screenshot">Screenshot (optional):</label>
          <div className="file-upload-area">
            <input
              type="file"
              id="screenshot"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <label htmlFor="screenshot" className="file-upload-button">
              📎 Choose Screenshot
            </label>
            <span className="file-upload-hint">
              PNG, JPG, GIF, BMP, WebP (max 10MB)
            </span>
          </div>
          
          {filePreview && (
            <div className="file-preview">
              <div className="preview-header">
                <span>Selected: {selectedFile?.name}</span>
                <button type="button" onClick={removeFile} className="remove-file-button">
                  ✕ Remove
                </button>
              </div>
              <img src={filePreview} alt="Preview" className="preview-image" />
            </div>
          )}
        </div>

        <button type="submit" className="submit-button">
          Submit Feedback
        </button>
      </form>
    </div>
  );
};

export default FeedbackForm;